import type {
  Identity,
  Alias,
  CreatedAlias,
  Context,
  BulkJob,
  FailedRow,
  TransformJobStatus,
  ApiResponse,
  BulkOperationResult,
  PaddleTier,
  PaddleBillingCycle,
  PaddleCheckoutResponse,
  UserSubscription,
  BillingUsage,
  Invoice,
  PlansResponse,
  PortalUrlResponse,
  UpgradeResponse,
  CancelResponse,
  DsrExportResult,
  DsrEraseResult,
  DsrAuditEntry,
} from '../types';

// All API requests are proxied through the Auth Service (/capi/*).
// The Auth Service validates the session cookie and injects the customer ID
// server-side before forwarding to the API worker via service binding.
const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:8787';
const API_PREFIX = `${AUTH_API_BASE}/capi`;

class VarinymAPIClient {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_PREFIX}${endpoint}`, {
      ...options,
      credentials: 'include', // Session cookie for Auth Service authentication
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (response.status === 401) {
      localStorage.removeItem('varinym-auth');
      const error = new Error('Unauthorized') as Error & { shouldRedirect: boolean };
      error.shouldRedirect = true;
      throw error;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ msg: 'Request failed' }));
      throw new Error(error.msg || error.error || 'API request failed');
    }

    return response.json();
  }

  // Identity Management
  async createIdentity(userId: string, contexts?: string[]): Promise<ApiResponse<{ id: string; aliases: CreatedAlias[] }>> {
    const contextQuery = contexts && contexts.length > 0 ? `?contexts=${contexts.join(',')}` : '';
    return this.request(`/create_identity/user/${encodeURIComponent(userId)}${contextQuery}`, { method: 'PUT' });
  }

  async getIdentity(userId: string): Promise<ApiResponse<Identity>> {
    return this.request(`/get_record/id/${encodeURIComponent(userId)}`);
  }

  // Alias Management
  // Idempotent: if an alias already exists for this user+context, returns
  // it (existing: true) instead of creating a duplicate.
  async addAlias(
    userId: string,
    context: string
  ): Promise<ApiResponse<{ alias_id: string; expires: number; existing?: boolean }>> {
    return this.request(`/add_alias/id/${encodeURIComponent(userId)}/context/${encodeURIComponent(context)}?include_expiry=true`, { method: 'PUT' });
  }

  async getAliases(userId: string, includeExpiry: boolean = false): Promise<ApiResponse<{ aliases: Alias[] }>> {
    const url = `/get_aliases/user/${encodeURIComponent(userId)}${includeExpiry ? '?include_expiry=true' : ''}`;
    // The backend serves this with Cache-Control: private, max-age=30, so a
    // refetch (e.g. after expire/delete) within that window can otherwise be
    // served straight from the browser's own HTTP cache. no-store forces a
    // real network request; it doesn't bypass the backend's edge cache.
    return this.request(url, { cache: 'no-store' });
  }

  async resolveAlias(aliasId: string): Promise<ApiResponse<{ user_id: string }>> {
    return this.request(`/resolve_id/alias/${encodeURIComponent(aliasId)}`);
  }

  async expireAlias(aliasId: string): Promise<ApiResponse> {
    return this.request(`/expire_alias/alias/${encodeURIComponent(aliasId)}`, { method: 'POST' });
  }

  // Issues a new alias_id for this user+context, irrespective of the
  // context's `rotates` setting or the current alias's expiry. The old
  // alias_id stops resolving as soon as this succeeds.
  async rotateAlias(userId: string, context: string): Promise<ApiResponse<{ alias_id: string; context: string; expires: number }>> {
    return this.request(`/rotate_alias/id/${encodeURIComponent(userId)}/context/${encodeURIComponent(context)}?include_expiry=true`, { method: 'POST' });
  }

  async deleteAlias(aliasId: string): Promise<ApiResponse> {
    return this.request(`/delete_alias/alias/${encodeURIComponent(aliasId)}`, { method: 'DELETE' });
  }

  // For an expired alias on a non-rotating context, alias_id reads back
  // as "0" (masked, unreachable by design), so there's no id to pass to
  // deleteAlias(). This targets the same row by user_id + context instead.
  async deleteAliasByUserContext(userId: string, context: string): Promise<ApiResponse> {
    return this.request(`/delete_alias/user/${encodeURIComponent(userId)}/context/${encodeURIComponent(context)}`, { method: 'DELETE' });
  }

  // DSR (GDPR data-subject requests)
  async exportUserData(userId: string): Promise<ApiResponse<DsrExportResult>> {
    return this.request(`/dsr/user/${encodeURIComponent(userId)}/export`);
  }

  async eraseUserData(userId: string): Promise<ApiResponse<DsrEraseResult>> {
    return this.request(`/dsr/user/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  }

  async getDsrAudit(params: { userId?: string; limit?: number; offset?: number }): Promise<
    ApiResponse<{ audit_log: DsrAuditEntry[]; count: number }>
  > {
    const query = new URLSearchParams();
    if (params.userId) query.set('user_id', params.userId);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.request(`/dsr/audit${qs ? `?${qs}` : ''}`);
  }

  // Context Management
  async listContexts(activeOnly = false): Promise<ApiResponse<{ contexts: Context[] }>> {
    const query = activeOnly ? '?active=true' : '';
    return this.request(`/contexts${query}`);
  }

  async getContext(name: string): Promise<ApiResponse<{ context: Context }>> {
    return this.request(`/contexts/${encodeURIComponent(name)}`);
  }

  async createContext(context: Omit<Context, 'id' | 'customer_id'>): Promise<ApiResponse<{ context: Context }>> {
    return this.request('/contexts', {
      method: 'POST',
      body: JSON.stringify(context),
    });
  }

  async updateContext(name: string, context: Partial<Omit<Context, 'id' | 'customer_id'>>): Promise<ApiResponse<{ context: Context }>> {
    return this.request('/contexts', {
      method: 'POST',
      body: JSON.stringify({ ...context, name }),
    });
  }

  // Bulk Operations - JSON
  async bulkCreateIdentities(
    userIds: string[],
    context?: string
  ): Promise<
    ApiResponse<{
      total: number;
      successful: number;
      failed: number;
      results: BulkOperationResult[];
    }>
  > {
    return this.request('/bulk/identities', {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds,
        ...(context && { contexts: [context] })
      }),
    });
  }

  async bulkCreateAliases(
    userIds: string[],
    context: string
  ): Promise<
    ApiResponse<{
      total: number;
      successful: number;
      failed: number;
      results: BulkOperationResult[];
    }>
  > {
    return this.request('/bulk/aliases', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, context }),
    });
  }

  async bulkResolveAliases(
    aliasIds: string[]
  ): Promise<
    ApiResponse<{
      total: number;
      successful: number;
      failed: number;
      results: BulkOperationResult[];
    }>
  > {
    return this.request('/bulk/resolve', {
      method: 'POST',
      body: JSON.stringify({ alias_ids: aliasIds }),
    });
  }

  // Bulk Operations - CSV
  async uploadCSV(file: File, contexts: string[]): Promise<
    ApiResponse<{
      job_id: string;
      file_key: string;
    }>
  > {
    const response = await fetch(
      `${API_PREFIX}/bulk/upload?contexts=${encodeURIComponent(contexts.join(','))}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'text/csv' },
        body: file, // File streams natively via fetch — do not read into memory first
      }
    );

    if (response.status === 401) {
      localStorage.removeItem('varinym-auth');
      const error = new Error('Unauthorized') as Error & { shouldRedirect: boolean };
      error.shouldRedirect = true;
      throw error;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ msg: 'Upload failed' }));
      throw new Error(error.msg || 'CSV upload failed');
    }

    return response.json();
  }

  // Bulk Operations - CSV Transform (console-only, proxied via Auth Service)
  async uploadTransform(
    file: File,
    config: {
      operation: 'replace' | 'resolve';
      column: string;
      context?: string;
      onFailure?: 'error_message' | 'keep_original';
    }
  ): Promise<{ status: string; msg: string; job_id: string; state: string }> {
    const params = new URLSearchParams({
      operation: config.operation,
      column: config.column,
    });
    if (config.operation === 'replace' && config.context) {
      params.set('context', config.context);
    }
    if (config.onFailure) {
      params.set('on_failure', config.onFailure);
    }

    const response = await fetch(`${API_PREFIX}/bulk/upload/transform?${params}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'text/csv' },
      body: file, // File streams natively via fetch — do not read into memory first
    });

    if (response.status === 401) {
      localStorage.removeItem('varinym-auth');
      const error = new Error('Unauthorized') as Error & { shouldRedirect: boolean };
      error.shouldRedirect = true;
      throw error;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ msg: 'Upload failed' }));
      throw new Error(error.msg || 'Transform upload failed');
    }

    return response.json();
  }

  async confirmTransform(jobId: string): Promise<ApiResponse> {
    return this.request(`/bulk/jobs/${jobId}/confirm`, { method: 'POST' });
  }

  async downloadTransformOutput(jobId: string): Promise<Response> {
    // Raw Response, not routed through request<T>() — it calls .json() unconditionally,
    // which breaks on a streamed CSV body, and callers need the status code to
    // distinguish 409/410/500 (each renders a different message).
    return fetch(`${API_PREFIX}/bulk/jobs/${jobId}/output`, {
      credentials: 'include',
    });
  }

  async getJobStatus(jobId: string): Promise<TransformJobStatus> {
    return this.request(`/bulk/jobs/${jobId}`);
  }

  async getJobResults(jobId: string): Promise<
    ApiResponse<{
      job_id: string;
      failed_count: number;
      failed_rows: FailedRow[];
      truncated?: boolean;
    }>
  > {
    return this.request(`/bulk/jobs/${jobId}/results`);
  }

  async listJobs(limit = 20, offset = 0): Promise<ApiResponse<{ jobs: BulkJob[]; count: number }>> {
    return this.request(`/bulk/jobs?limit=${limit}&offset=${offset}`);
  }

  // Statistics
  // Counts are server-side cached for 24h and may lag reality by that much
  // under normal use; `checked_at` (unix seconds) reflects when the DB was
  // actually queried and is accurate even on a cache hit.
  async countIdentities(options?: { refresh?: boolean }): Promise<ApiResponse<{ count: number; checked_at: number }>> {
    const query = options?.refresh ? '?refresh=true' : '';
    return this.request(`/count_identities${query}`);
  }

  async countAliases(options?: { refresh?: boolean }): Promise<ApiResponse<{ count: number; checked_at: number }>> {
    const query = options?.refresh ? '?refresh=true' : '';
    return this.request(`/count_aliases${query}`);
  }

  async getStatistics(): Promise<{
    identities: number;
    aliases: number;
    identitiesCheckedAt?: number;
    aliasesCheckedAt?: number;
  }> {
    const [identitiesRes, aliasesRes] = await Promise.all([
      this.countIdentities(),
      this.countAliases(),
    ]);

    return {
      identities: identitiesRes.count || 0,
      aliases: aliasesRes.count || 0,
      identitiesCheckedAt: identitiesRes.checked_at,
      aliasesCheckedAt: aliasesRes.checked_at,
    };
  }

  // Billing / Paddle
  async createCheckoutSession(
    tier: PaddleTier,
    billing_cycle: PaddleBillingCycle,
    email: string
  ): Promise<PaddleCheckoutResponse> {
    return this.request('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ tier, billing_cycle, email }),
    });
  }

  async getSubscription(): Promise<UserSubscription> {
    return this.request('/billing/subscription');
  }

  async getUsage(): Promise<BillingUsage> {
    return this.request('/billing/usage');
  }

  async getInvoices(): Promise<{ invoices: Invoice[] }> {
    return this.request('/billing/invoices');
  }

  async getPortalUrl(): Promise<PortalUrlResponse> {
    return this.request('/billing/portal');
  }

  async upgradePlan(
    tier: PaddleTier,
    billing_cycle: PaddleBillingCycle
  ): Promise<UpgradeResponse> {
    return this.request('/billing/upgrade', {
      method: 'POST',
      body: JSON.stringify({ tier, billing_cycle }),
    });
  }

  async cancelSubscription(): Promise<CancelResponse> {
    return this.request('/billing/cancel', {
      method: 'POST',
    });
  }

  async getPlans(): Promise<PlansResponse> {
    return this.request('/billing/plans');
  }

  async getInvoicePdfUrl(pdfPath: string): Promise<string> {
    const { url } = await this.request<{ url: string }>(pdfPath);
    return url;
  }
}

export const varinymAPI = new VarinymAPIClient();
